import { create } from "zustand";
import { persist } from "zustand/middleware";

// import { PageMeta } from "../hooks/pageHooks/usePageDetails";

type DataType = Record<string, any>;

interface TableStoreState {
	// pageMeta: PageMeta | null;
	modalTitle: string;
	openRowUuid: string | null;
	setOpenRowUuid: (rowUuid: string | null) => void;
	isModalOpen: boolean;
	activeIndex: number | null;
	activeSortColumn: string | null;
	activeUuid: string | null;
	searchDropdownValue: string | null;
	searchColumn: string;
	switchContent: boolean;
	data: DataType[];
	activeRowUuid: string | null;
	deactivatedQuickMenuIds: string[];
	searchCriteria: any[];
	hasSort: boolean;
	hasModalTable: boolean;
	selectedRowId: string | null;
	resetFilters: boolean;
	showResetFiltersButton: boolean;
	isQuickActionMenuOpen: boolean;
	tablePage: number;
	combinedTableData: DataType[];
	tableIsLoading: boolean;
	slug: string | null;

	// Actions
	// setPageMeta: (meta: PageMeta | null) => void;
	setModalTitle: (title: string) => void;
	setHasModalTable: (value: boolean) => void;
	setData: (data: DataType[]) => void;
	setIsModalOpen: (value: boolean) => void;
	setIsQuickActionMenuOpen: (value: boolean) => void;
	toggleModal: () => void;
	setActiveIndex: (index: number | null) => void;
	setActiveSortColumn: (columnId: string | null) => void;
	setActiveUuid: (uuid: string | null) => void;
	setSearchDropdownValue: (value: string | null) => void;
	setSearchColumn: (value: string) => void;
	setSwitchContent: (value: boolean) => void;
	setActiveRowUuid: (value: string | null) => void;
	setDeactivatedQuickMenuIds: (ids: string[]) => void;
	setSearchCriteria: (criteria: any[]) => void;
	setHasSort: (value: boolean) => void;
	setSelectedRowId: (id: string | null) => void;
	setTablePage: (page: number) => void;
	setCombinedTableData: (data1: DataType[], data2: DataType[]) => void;
	handleRowClick: (
		index: number,
		rowUuid: string,
		hasModalTable: boolean,
		data: DataType[]
	) => void;
	setResetFilters: (value: boolean | ((prev: boolean) => boolean)) => void;
	setShowResetFiltersButton: (value: boolean) => void;
	setTableIsLoading: (value: boolean) => void;
	setSlug: (slug: string | null) => void;
}

export const useTableStore = create<TableStoreState>()(
	persist(
		(set, get) => ({
			// Initial States
			pageMeta: null,
			modalTitle: "",
			isModalOpen: false,
			activeIndex: null,
			activeSortColumn: null,
			activeUuid: null,
			searchDropdownValue: null,
			searchColumn: "",
			switchContent: false,
			data: [],
			activeRowUuid: null,
			deactivatedQuickMenuIds: [],
			searchCriteria: [],
			hasSort: false,
			hasModalTable: false,
			selectedRowId: null,
			resetFilters: false,
			tablePage: 1,
			combinedTableData: [],
			showResetFiltersButton: false,
			openRowUuid: null,
			isQuickActionMenuOpen: false,
			tableIsLoading: false,
			slug: null,

			setIsQuickActionMenuOpen(value) {
				set({ isQuickActionMenuOpen: value });
			},
			setOpenRowUuid: (rowUuid) => {
				set({ openRowUuid: rowUuid });
			},
			// Setters and actions
			// setPageMeta: (meta) => set({ pageMeta: meta }),
			setModalTitle: (title) => set({ modalTitle: title }),
			setHasModalTable: (value) => set({ hasModalTable: value }),
			setData: (d) => set({ data: d }),
			setIsModalOpen: (value) => set({ isModalOpen: value }),
			toggleModal: () =>
				set((state) => ({ isModalOpen: !state.isModalOpen })),
			setActiveIndex: (index) => set({ activeIndex: index }),
			setActiveSortColumn: (columnId) =>
				set({ activeSortColumn: columnId }),
			setActiveUuid: (uuid) => set({ activeUuid: uuid }),
			setSearchDropdownValue: (value) =>
				set({ searchDropdownValue: value }),
			setSearchColumn: (value) => set({ searchColumn: value }),
			setSwitchContent: (value) => set({ switchContent: value }),
			setActiveRowUuid: (value) => set({ activeRowUuid: value }),
			setDeactivatedQuickMenuIds: (ids) =>
				set({ deactivatedQuickMenuIds: ids }),
			setSearchCriteria: (criteria) => set({ searchCriteria: criteria }),
			setHasSort: (value) => set({ hasSort: value }),
			setSelectedRowId: (id) => set({ selectedRowId: id }),
			setTablePage: (page) => set({ tablePage: page }),
			setCombinedTableData: (currentData, newData) => {
				set((state) => {
					const combined = [...currentData, ...newData];

					if (
						JSON.stringify(state.combinedTableData) !==
						JSON.stringify(combined)
					) {
						return { combinedTableData: combined };
					}
					return state;
				});
			},

			handleRowClick: (index, rowUuid, hasModalTable, data) => {
				const currentState = get();
				const toggleModal = currentState.toggleModal;
				set({ activeIndex: index });
				toggleModal();
				set({ activeUuid: rowUuid, switchContent: hasModalTable });

				const itemUuid =
					data &&
					data[index] &&
					data[index].data &&
					data[index].data.uuid
						? data[index].data.uuid
						: rowUuid;
				set({ activeUuid: itemUuid });
			},
			setResetFilters: (value) =>
				set((state) => ({
					resetFilters:
						typeof value === "function"
							? value(state.resetFilters)
							: value,
				})),
			setShowResetFiltersButton: (value) =>
				set({ showResetFiltersButton: value }),
			setTableIsLoading: (value) => set({ tableIsLoading: value }),
			setSlug: (slug) => set({ slug }),
		}),
		{
			name: "zu-table-store",
			partialize: (state) => ({
				// Persist searchCriteria as before
				searchCriteria: state.searchCriteria,
			}),
		}
	)
);

export default useTableStore;
